import * as React from 'react';

interface UseVirtualizedLoadMoreProps {
  /**
   * Number of items currently loaded in memory
   */
  dataLength: number;
  /**
   * Index of the last visible virtual item (from virtualizer.getVirtualItems())
   */
  lastVirtualIndex: number;
  /**
   * Whether the next page is currently being fetched
   */
  isFetchingNextPage?: boolean;
  /**
   * Callback to load more data. Stored in a ref internally so reference
   * changes do not re-trigger the effect.
   */
  onLoadMore?: () => void;
}

/**
 * Triggers `onLoadMore` when the virtualizer's visible range reaches the
 * end of the loaded data. Keeps `onLoadMore` in a ref so an unstable
 * callback reference (e.g. an inline ternary) cannot cause an infinite
 * update loop.
 */
function useVirtualizedLoadMore(props: UseVirtualizedLoadMoreProps): void {
  const { dataLength, lastVirtualIndex, isFetchingNextPage, onLoadMore } = props;

  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  React.useEffect(() => {
    if (dataLength === 0) return;

    const lastDataIndex = dataLength - 1;
    if (onLoadMoreRef.current && !isFetchingNextPage && lastVirtualIndex >= lastDataIndex) {
      onLoadMoreRef.current();
    }
  }, [lastVirtualIndex, dataLength, isFetchingNextPage]);
}

export { useVirtualizedLoadMore };
export type { UseVirtualizedLoadMoreProps };
